// AM Advertising Home — deep links into the analysis surfaces. PURE.
//
// The two reporting dashboards accept a `?accountId=` mount seed (added with
// this page), so an alert / book row lands focused on that account. The account
// page is the fallback destination for anything account-shaped.

import type { AdChannel } from '../../types/advertisingHome';
import { normalizeCustomerId } from '../../lib/googleAdsAttribution';
import { googleAdsCampaignsUrl } from '../../lib/googleAdsAccountLink';

export function metaReportingHref(accountId: string): string {
  return `/reporting?accountId=${encodeURIComponent(accountId)}`;
}

export function googleReportingHref(accountId: string): string {
  return `/reporting/google-ads?accountId=${encodeURIComponent(accountId)}`;
}

export function channelReportingHref(channel: AdChannel, accountId: string): string {
  return channel === 'meta' ? metaReportingHref(accountId) : googleReportingHref(accountId);
}

export function accountHref(accountId: string): string {
  return `/accounts/${encodeURIComponent(accountId)}`;
}

/**
 * The Meta explorer focused as close to an ad as its params allow — the account
 * filter plus the ad's campaign as a name-agnostic hint is not expressible in
 * /reporting's params today, so the account seed is the honest landing.
 */
export function creativeHref(accountId: string): string {
  return metaReportingHref(accountId);
}

// ── The EXTERNAL ad accounts — where the fix actually happens ───────────────
// The kyck dashboards explain WHAT is wrong; these are WHERE you go to change
// it, which is what makes the status popover a complete unit rather than a
// pointer to another read-only screen.

/**
 * Meta Ads Manager, scoped to the ad ACCOUNT (the campaign list). This is the
 * account-level sibling of the metaAds module's ad-specific deep link — kept
 * here rather than imported, since a frontend module reaches other modules only
 * through their barrels, and this is one line of URL.
 *
 * ⚠️ `act_` is stripped: `act=` wants the bare digits, and the roster stores the
 * id RAW (no prefix) while Zernio wants it prefixed — so accept either.
 */
export function metaAdsManagerHref(fbAdAccountId: string | null): string | null {
  if (!fbAdAccountId) return null;
  const act = fbAdAccountId.replace(/^act_/, '').trim();
  if (act === '') return null;
  return `https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${encodeURIComponent(act)}`;
}

/** Google Ads, scoped to the customer. Shares the ONE constructed-link helper. */
export function googleAdsHref(googleAdsCustomerId: string | null): string | null {
  const digits = normalizeCustomerId(googleAdsCustomerId);
  return digits ? googleAdsCampaignsUrl(digits) : null;
}
